import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

interface CredentialStatusResponse {
  readonly has_sp_api: boolean;
  readonly has_ads_api: boolean;
  readonly has_anthropic: boolean;
  readonly last_validation_status: string;
  readonly last_validated_at: string | null;
}

/**
 * Guard that checks whether the tenant has SP-API / Ads API credentials configured.
 * Runs AFTER subscriptionGuard, so trial/subscription issues should already be handled.
 *
 * Belt-and-suspenders: if the backend returns 402 (trial expired) or 403 (subscription
 * inactive), redirect to /billing instead of /connect-store — the user needs to subscribe
 * before they can set up credentials.
 */
export const apiKeysGuard: CanActivateFn = async () => {
  const http = inject(HttpClient);
  const router = inject(Router);

  // Check platform_connections table first (new LWA OAuth flow)
  try {
    const platformStatus = await firstValueFrom(
      http.get<Record<string, { status: string }>>(
        `${environment.sellerApiUrl}/v1/platforms/status`,
      ),
    );
    const hasPlatformConnection =
      platformStatus['amazon_sp_api']?.status === 'connected' ||
      platformStatus['amazon_ads_api']?.status === 'connected';
    if (hasPlatformConnection) return true;
  } catch (err: unknown) {
    // If trial expired or subscription inactive, redirect to billing
    if (err instanceof HttpErrorResponse && (err.status === 402 || err.status === 403)) {
      return router.createUrlTree(['/billing']);
    }
    // Fall through to legacy credentials check
  }

  // Legacy credentials table check
  try {
    const status = await firstValueFrom(
      http.get<CredentialStatusResponse>(
        `${environment.sellerApiUrl}/credentials/status`,
      ),
    );
    if (!status.has_sp_api || !status.has_ads_api) {
      return router.createUrlTree(['/connect-store']);
    }
    return true;
  } catch (err: unknown) {
    // If trial expired or subscription inactive, redirect to billing
    if (err instanceof HttpErrorResponse && (err.status === 402 || err.status === 403)) {
      return router.createUrlTree(['/billing']);
    }
    return router.createUrlTree(['/connect-store']);
  }
};
